// src/hubspot.js
// All HubSpot reads and writes go through this module.
// Calls /api/hubspot (Azure Function proxy -- token stays server-side).
//
// CRITICAL: HubSpot rate limit is ~110 requests / 10 seconds.
// We aggressively throttle to stay well under that ceiling.

const API_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL + "/hubspot"
  : "/api/hubspot";

const BATCH_SIZE = 100;            // companies per page (HubSpot max)
const BATCH_DELAY_MS = 350;        // ms between company-list pages
const ENRICH_CONCURRENCY = 6;      // companies enriched in parallel
const ENRICH_BATCH_DELAY_MS = 250; // ms between batches of N concurrent
const MAX_RETRIES = 5;             // for 429 errors
const GLOBAL_CONCURRENCY = 2;      // max in-flight HubSpot requests app-wide

const sleep = ms => new Promise(r => setTimeout(r, ms));

// ── Global concurrency limiter ────────────────────────────────────────────────
// All HubSpot requests go through this queue. When 32+ requests are fired at
// once (e.g. Team Scoreboard YTD load: 4 metrics x 8 reps), only 5 actually
// run at a time. The rest wait. Once one finishes another starts. This
// prevents the "rate limit exceeded after 5 retries" cascade that happens
// when too many requests collide trying to retry simultaneously.
let activeRequests = 0;
const pendingQueue = [];

function acquireSlot() {
  return new Promise(resolve => {
    if (activeRequests < GLOBAL_CONCURRENCY) {
      activeRequests++;
      resolve();
    } else {
      pendingQueue.push(resolve);
    }
  });
}

function releaseSlot() {
  if (pendingQueue.length > 0) {
    const next = pendingQueue.shift();
    next();  // activeRequests stays the same -- they swap places
  } else {
    activeRequests = Math.max(0, activeRequests - 1);
  }
}

// Wraps fetch with automatic 429 retry. HubSpot tells us how long to wait
// via the Retry-After header (seconds) -- we honor it, with sane fallbacks.
async function call(action, opts = {}) {
  await acquireSlot();
  try {
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, ...opts }),
      });
      if (res.status === 429) {
        const retryAfter = parseInt(res.headers.get("Retry-After") || "0");
        // Start at 4s, double each retry, cap at 60s. HubSpot's bucket needs
        // ~10s to refill; starting at 4s gives us breathing room before
        // hammering again. With GLOBAL_CONCURRENCY=2 this should rarely trigger.
        const waitMs = retryAfter > 0 ? retryAfter * 1000 : Math.min(60000, 4000 * Math.pow(2, attempt));
        console.warn("[HubSpot] Rate limited (429). Waiting " + waitMs + "ms before retry " + (attempt+1) + "/" + MAX_RETRIES + "...");
        await sleep(waitMs);
        continue;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || ("HubSpot API error " + res.status));
      }
      return res.json();
    }
    throw new Error("HubSpot rate limit exceeded after " + MAX_RETRIES + " retries");
  } finally {
    releaseSlot();
  }
}

// ── STAGE MAPPING ─────────────────────────────────────────────────────────────
// We support multiple HubSpot pipelines (Mattress + ABM CNC/Quilter). HubSpot
// accounts often have additional default/legacy pipelines too. We dynamically
// discover all stages on app load via getAllPipelines() so:
//   - all stage IDs get a display name (no raw IDs leaking into the UI)
//   - all stage IDs get classified by pipeline (no deal silently mis-categorized)
//
// As a fallback (in case the pipeline discovery API fails or hasn't run yet),
// we keep a static map with the known ABM stages we explicitly support. The
// Mattress (Gribetz/Porter/Galkin) stages get populated by discoverPipelines()
// on app boot - they're not hardcoded here because their IDs aren't documented
// in code, only in the HubSpot account configuration.
const STATIC_STAGE_MAP = {
  // ABM CNC/Quilter pipeline (we hardcode these since they appeared in earlier
  // diagnostics; discoverPipelines() will refresh / overwrite anyway).
  "668946997":  "Lead Inquired or Shows Interest",
  "981909633":  "Quote Sent",
  "981909637":  "Closed Won",
};
const STATIC_STAGE_TO_PIPELINE = {
  "668946997":  "abm",
  "981909633":  "abm",
  "981909637":  "abm",
};

// Dynamic maps populated by getAllPipelines() at app boot. Falls back to
// STATIC maps until/unless the API call succeeds.
let DYNAMIC_STAGE_MAP = {};
let DYNAMIC_STAGE_TO_PIPELINE = {};

// Combined view -- dynamic overrides static (static is the safety net)
function STAGE_MAP_LOOKUP(stageId) {
  return DYNAMIC_STAGE_MAP[stageId] || STATIC_STAGE_MAP[stageId] || stageId || "";
}
function PIPELINE_LOOKUP(stageId) {
  return DYNAMIC_STAGE_TO_PIPELINE[stageId] || STATIC_STAGE_TO_PIPELINE[stageId] || "other";
}

// Reverse maps for writing display name -> stage ID. Multiple pipelines have
// stages with the same display name ("Quote Sent", "Closed Won") so we need to
// be pipeline-aware when writing back to HubSpot.
//
// These start empty and get populated by discoverPipelines() at app boot.
// As a safety net for the ABM pipeline (whose stage IDs we've observed and
// hardcoded) we include those statically — Mattress IDs cannot be hardcoded
// because they vary by HubSpot account.
let DYNAMIC_REVERSE_MATTRESS = {};
let DYNAMIC_REVERSE_ABM = {
  // Static fallback for ABM pipeline based on the user's HubSpot account.
  // Will be overwritten by discoverPipelines() with the latest data.
  "Lead Inquired or Shows Interest": "668946997",
  "Quote Sent":                      "981909633",
  "Closed Won":                      "981909637",
};

// ── Pipeline discovery ───────────────────────────────────────────────────────
// Fetches all deal pipelines + their stages from HubSpot, builds dynamic
// stage→display-name and stage→pipeline-id maps.
//
// Pipeline classification: maps HubSpot pipeline IDs to our internal pipeline
// names ("mattress" | "abm" | "other"). Anything not listed is "other" and
// won't appear in the CRM's Pipeline tab.
//
// To find a pipeline's ID, run in browser console on the live site:
//   fetch('/api/hubspot', {method:'POST', headers:{'Content-Type':'application/json'},
//     body: JSON.stringify({action: 'pipelines.list'})})
//     .then(r=>r.json()).then(d => d.results.forEach(p =>
//       console.log(p.label, '|', p.id)));
// Map HubSpot pipeline IDs -> our internal pipeline bucket. Only pipelines
// listed here count toward Sales Generated; anything not listed is treated as
// "other" and excluded from quota calculations.
//
// Bucket IDs here must match SALES_PIPELINES[].id in companyBuckets.js -- the
// Pipeline-visibility per-user feature uses these strings to gate access.
//
// EXPLICITLY EXCLUDED (per business request -- these aren't product sales):
//   - default            | Sales Pipeline (default HubSpot pipeline, generic)
//   - 891321730          | Service Pipeline (post-sale service work)
//   - 75e28846-...       | Ecommerce Pipeline (small online orders)
//   - 898314672          | ONBOARDING - INNOVA (post-sale onboarding milestone)
const PIPELINE_ID_MAP = {
  "723482160": "mattress",          // Gribetz/Porter/Galkin
  "668946997": "abm",               // ABM - CNC Quilter/Perf/ROBO
  "837687416": "aac",               // AAC
  "70448042":  "necchi",            // NECCHI
  "669263703": "newdealer_necchi",  // NEW DEALER - NECCHI
  "669306963": "newdealer_innova",  // NEW DEALER - INNOVA
  "115832391": "dealerlead",        // Dealer Lead Pipeline
};

function classifyPipelineByHubSpotId(pipelineId) {
  return PIPELINE_ID_MAP[pipelineId] || "other";
}

// Dynamic list of "Closed Won" stage IDs across the Mattress + ABM pipelines.
// Populated by discoverPipelines(). Used by getSalesGenerated().
let DYNAMIC_CLOSED_WON_IDS = [];

export function getClosedWonStageIds() {
  return DYNAMIC_CLOSED_WON_IDS.length > 0
    ? DYNAMIC_CLOSED_WON_IDS
    : ["981909637"]; // ABM Closed Won as last-resort fallback
}

export async function discoverPipelines() {
  try {
    const res = await call("pipelines.list");
    const pipelines = res.results || [];
    const newStageMap = {};
    const newStageToPipeline = {};
    const newReverseMattress = {};
    const newReverseAbm = {};
    const closedWonIds = [];
    for (const p of pipelines) {
      const pipelineId = classifyPipelineByHubSpotId(p.id);
      for (const s of (p.stages || [])) {
        newStageMap[s.id] = s.label;
        newStageToPipeline[s.id] = pipelineId;
        // Build reverse maps for writing display name -> stage ID. Only for
        // mattress + abm; other pipelines aren't editable from our CRM.
        if (pipelineId === "mattress") newReverseMattress[s.label] = s.id;
        if (pipelineId === "abm")      newReverseAbm[s.label]      = s.id;
        // Collect Closed Won stages from all pipelines we recognize as real
        // sales (mattress, abm, homequilting, dealerlead). Pipelines that
        // return "other" from the classifier (Sales/Service/Ecommerce/
        // Onboarding) are deliberately excluded -- they're not product sales.
        const isRealSalesPipeline = pipelineId !== "other";
        const isWonStage = isRealSalesPipeline && (
          s.metadata?.probability === "1.0" ||
          s.metadata?.probability === 1.0 ||
          /closed\s*won/i.test(s.label || "")
        );
        if (isWonStage) closedWonIds.push(s.id);
      }
    }
    DYNAMIC_STAGE_MAP = newStageMap;
    DYNAMIC_STAGE_TO_PIPELINE = newStageToPipeline;
    DYNAMIC_REVERSE_MATTRESS = newReverseMattress;
    // Preserve static ABM fallback if dynamic discovery returned empty for ABM
    if (Object.keys(newReverseAbm).length > 0) DYNAMIC_REVERSE_ABM = newReverseAbm;
    DYNAMIC_CLOSED_WON_IDS = closedWonIds;
    console.log("[HubSpot] Discovered " + pipelines.length + " pipelines, " + Object.keys(newStageMap).length + " stages, " + closedWonIds.length + " Closed Won stages");
    return pipelines;
  } catch (e) {
    console.warn("Pipeline discovery failed, using static fallback:", e.message);
    return [];
  }
}
// Normalize a date value from HubSpot to YYYY-MM-DD (or "" if unparseable).
// HubSpot can return: "2026-03-15", ISO strings, epoch ms, or empty.
function normalizeDate(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  // Already in YYYY-MM-DD format -- keep as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Try parsing -- handles ISO strings and epoch ms (numeric strings)
  const ms = /^\d+$/.test(s) ? parseInt(s) : Date.parse(s);
  if (isNaN(ms)) return "";
  const d = new Date(ms);
  if (isNaN(d.getTime())) return "";
  // Format as YYYY-MM-DD using UTC parts to avoid timezone shifts
  return d.getUTCFullYear() + "-" +
         String(d.getUTCMonth() + 1).padStart(2, "0") + "-" +
         String(d.getUTCDate()).padStart(2, "0");
}

function normalizeStage(hsStage) {
  if (!hsStage) return "Quote Requested";
  return STAGE_MAP_LOOKUP(hsStage) || hsStage;
}
// Return the pipeline ID ("mattress" | "abm" | "other") this stage belongs to.
// Exported so components can classify deals fetched before pipeline discovery.
export function pipelineForStage(hsStage) {
  return PIPELINE_LOOKUP(hsStage);
}
function toHubSpotStage(displayName) {
  return DYNAMIC_REVERSE_MATTRESS[displayName] || displayName;
}
// Pipeline-aware: when moving a deal between stages in the ABM pipeline,
// we need to use ABM stage IDs (e.g. "Closed Won" -> 981909637, not whatever
// the Mattress pipeline's Closed Won ID happens to be).
function toHubSpotStageForPipeline(displayName, pipeline) {
  if (pipeline === "abm") {
    return DYNAMIC_REVERSE_ABM[displayName] || DYNAMIC_REVERSE_MATTRESS[displayName] || displayName;
  }
  return DYNAMIC_REVERSE_MATTRESS[displayName] || displayName;
}

// ── COMPANY TYPES ─────────────────────────────────────────────────────────────
export async function getCompanyTypes() {
  try {
    const res = await call("properties.company");
    const props = res.results || res || [];
    const typeProp = Array.isArray(props) ? props.find(p => p.name === "type") : null;
    if (!typeProp || !typeProp.options) return [];
    return typeProp.options.map(o => ({
      label: o.label || o.value || "",
      value: o.value || o.label || "",
    }));
  } catch (e) {
    console.warn("getCompanyTypes failed:", e.message);
    return [];
  }
}

// ── COMPANIES ─────────────────────────────────────────────────────────────────
//
// Returns BASIC company records only (no contacts or deals).
// Call enrichCompanies() afterward to load relationships in the background.
export async function getCompanies(allowedTypes = [], onProgress) {
  const allCompanies = [];
  let after = null;

  // Normalize types -- strip to just the internal VALUE for filtering.
  const typeValues = (allowedTypes || [])
    .map(t => (typeof t === "string" ? t : t.value || t.label || ""))
    .filter(Boolean);

  // Single filterGroup with IN operator -- handles unlimited types
  // (HubSpot's 5-filterGroup limit makes the per-type approach unworkable).
  const filterGroups = typeValues.length
    ? [{ filters: [{ propertyName: "type", operator: "IN", values: typeValues }] }]
    : [{ filters: [] }];

  while (true) {
    const searchBody = {
      filterGroups,
      properties: ["name","city","state","phone","address","zip","type","hubspot_owner_id"],
      limit: BATCH_SIZE,
      ...(after ? { after } : {}),
    };

    let res;
    try {
      res = await call("companies.search", { data: searchBody });
    } catch (e) {
      console.error("Company search failed:", e.message);
      throw e;
    }

    const results = res.results || [];
    allCompanies.push(...results);

    const total = res.total || allCompanies.length;
    if (onProgress) onProgress(allCompanies.length, total);

    after = res.paging?.next?.after || null;
    if (!after || results.length === 0) break;

    await sleep(BATCH_DELAY_MS);
  }

  return allCompanies.map(co => ({
    id: co.id,
    properties: {
      name:    co.properties.name    || "",
      city:    co.properties.city    || "",
      state:   co.properties.state   || "",
      phone:   co.properties.phone   || "",
      address: co.properties.address || "",
      zip:     co.properties.zip     || "",
      type:    co.properties.type    || "",
    },
    ownerId:  co.properties.hubspot_owner_id || null,
    contacts: [],
    deals:    [],
  }));
}

// Background enrichment: load contacts + deals for each company.
// Processes ENRICH_CONCURRENCY companies in parallel per batch, with a small
// delay between batches. With 4 concurrent and 300ms delay, we average around
// ~200 requests/min -- well under HubSpot's 660/min ceiling but ~4x faster
// than purely sequential. The 429 retry logic in `call()` handles any spikes.
//
// Skips companies that already have contacts OR deals populated -- so re-syncs
// or page reloads don't re-fetch everything from scratch.
//
// Accepts an optional abortRef = {current: false}. If abortRef.current becomes
// true mid-loop, enrichment stops cleanly at the next batch boundary.
export async function enrichCompanies(companies, onBatchDone, abortRef) {
  const enriched = [...companies];
  const total = enriched.length;
  let processed = 0;

  for (let i = 0; i < total; i += ENRICH_CONCURRENCY) {
    if (abortRef && abortRef.current) {
      console.log("[HubSpot] Enrichment aborted at company " + i + " of " + total);
      if (onBatchDone) onBatchDone([...enriched], i, total, true);
      return enriched;
    }

    const batch = enriched.slice(i, i + ENRICH_CONCURRENCY);

    await Promise.all(batch.map(async (co) => {
      // Skip if already enriched
      const alreadyEnriched = (co.contacts && co.contacts.length > 0) || (co.deals && co.deals.length > 0);
      if (alreadyEnriched) return;

      try {
        // Within one company, fire contacts and deals in parallel -- they're
        // independent. This is 2 requests per company, so 4 concurrent
        // companies means up to 8 requests in flight at once. Still safe.
        const [contacts, deals] = await Promise.all([
          getContactsForCompany(co.id).catch(() => []),
          getDealsForCompany(co.id).catch(() => []),
        ]);
        co.contacts = contacts;
        co.deals    = deals;
      } catch (e) {
        console.warn("Enrich failed for company " + co.id + ":", e.message);
      }
    }));

    processed = Math.min(i + ENRICH_CONCURRENCY, total);

    // Notify parent every batch (which is roughly every 4 companies)
    if (onBatchDone) onBatchDone([...enriched], processed, total, false);

    // Pause between batches to spread requests over time
    if (processed < total) await sleep(ENRICH_BATCH_DELAY_MS);
  }

  if (onBatchDone) onBatchDone([...enriched], total, total, false);
  return enriched;
}

export async function updateCompanyOwner(coId, ownerId) {
  return call("companies.update", {
    id: coId,
    data: { properties: { hubspot_owner_id: ownerId } },
  });
}

// Look for an existing company that matches name+city+state. Used to prevent
// duplicate creation by sales reps. Matching is case-insensitive and
// whitespace-normalized. Returns the matched company or null.
//
// Implementation note: we do the city/state comparison client-side instead of
// adding more filters to HubSpot's search, because HubSpot string filters are
// case-sensitive and don't normalize whitespace. By over-fetching name matches
// and filtering locally, we get reliable comparison.
export async function findDuplicateCompany(name, city, state) {
  const normName = String(name || "").trim().toLowerCase().replace(/\s+/g, " ");
  const normCity = String(city || "").trim().toLowerCase();
  const normState = String(state || "").trim().toLowerCase();
  if (!normName) return null;

  try {
    const res = await call("companies.search", {
      data: {
        filterGroups: [{
          filters: [
            { propertyName: "name", operator: "CONTAINS_TOKEN", value: normName.split(" ")[0] },
          ],
        }],
        properties: ["name", "city", "state"],
        limit: 50,
      },
    });
    const candidates = res.results || [];
    return candidates.find(c => {
      const cName  = String(c.properties.name  || "").trim().toLowerCase().replace(/\s+/g, " ");
      const cCity  = String(c.properties.city  || "").trim().toLowerCase();
      const cState = String(c.properties.state || "").trim().toLowerCase();
      // Treat 2-letter state codes as equivalent to full names (TX vs Texas).
      // Accept either: exact match, first-2-chars match, or one is prefix of other.
      const stateMatch = cState === normState
        || cState.slice(0, 2) === normState.slice(0, 2)
        || cState.startsWith(normState)
        || normState.startsWith(cState);
      return cName === normName && cCity === normCity && stateMatch;
    }) || null;
  } catch (e) {
    console.warn("Duplicate company check failed:", e.message);
    return null;  // fail-open: don't block creation if duplicate check errors
  }
}

// Create a new company in HubSpot. Returns the created company in normalized
// shape (matching what getCompanies returns) so the caller can append to local
// state immediately. Caller is responsible for calling findDuplicateCompany
// first to surface duplicates.
export async function createCompany(fields, ownerId) {
  const properties = {
    name:    String(fields.name    || "").trim(),
    city:    String(fields.city    || "").trim(),
    state:   String(fields.state   || "").trim(),
    zip:     String(fields.zip     || "").trim(),
    phone:   String(fields.phone   || "").trim(),
    address: String(fields.address || "").trim(),
    type:    String(fields.type    || "").trim(),  // raw HubSpot value, not friendly label
  };
  if (ownerId) properties.hubspot_owner_id = String(ownerId);

  const res = await call("companies.create", { data: { properties } });
  if (!res?.id) throw new Error("HubSpot did not return a company ID");

  // Return in the same shape as getCompanies() so caller can shove into state
  return {
    id: res.id,
    properties: {
      name:    properties.name,
      city:    properties.city,
      state:   properties.state,
      zip:     properties.zip,
      phone:   properties.phone,
      address: properties.address,
      type:    properties.type,
      ownerId: ownerId || "",
    },
    contacts: [],
    deals:    [],
  };
}

// ── CONTACTS ─────────────────────────────────────────────────────────────────
export async function getContactsForCompany(companyId) {
  const assoc = await call("contacts.forCompany", {
    id: companyId,
    params: { limit: 100 },
  });
  const ids = (assoc.results || []).map(r => r.id);
  if (!ids.length) return [];

  // Sequential fetch with delay -- avoids burst when a company has many contacts
  const contacts = [];
  for (const id of ids) {
    const c = await call("contacts.get", {
      id,
      params: { properties: "firstname,lastname,email,phone,jobtitle,hubspot_owner_id" },
    }).catch(() => null);
    if (c) contacts.push(c);
    if (ids.length > 5) await sleep(150);
  }

  return contacts.map(c => ({
    id:      c.id,
    name:    [c.properties.firstname, c.properties.lastname].filter(Boolean).join(" ") || c.properties.email || "Unnamed",
    email:   c.properties.email    || "",
    phone:   c.properties.phone    || "",
    role:    c.properties.jobtitle || "",
    ownerId: c.properties.hubspot_owner_id || null,
  }));
}

// Look up a contact by email address. Case-insensitive. Returns the contact
// (with company association info) or null if not found.
//
// IMPORTANT: HubSpot stores emails case-insensitively for uniqueness, but the
// stored representation may be the original case. We search with lowercase
// which HubSpot handles correctly.
export async function findContactByEmail(email) {
  const norm = String(email || "").trim().toLowerCase();
  if (!norm) return null;
  try {
    const res = await call("contacts.search", {
      data: {
        filterGroups: [{
          filters: [
            { propertyName: "email", operator: "EQ", value: norm },
          ],
        }],
        properties: ["email", "firstname", "lastname", "phone"],
        limit: 1,
      },
    });
    return res.results?.[0] || null;
  } catch (e) {
    console.warn("findContactByEmail failed:", e.message);
    return null;
  }
}

// Create a new contact in HubSpot and associate to a company. Returns the
// created contact in the normalized shape used elsewhere in the app.
export async function createContact(fields, companyId) {
  const properties = {
    firstname: String(fields.firstName || "").trim(),
    lastname:  String(fields.lastName  || "").trim(),
    email:     String(fields.email     || "").trim().toLowerCase(),
    phone:     String(fields.phone     || "").trim(),
    jobtitle:  String(fields.role      || fields.jobTitle || "").trim(),
  };

  const res = await call("contacts.create", { data: { properties } });
  if (!res?.id) throw new Error("HubSpot did not return a contact ID");

  // Associate contact -> company if a companyId was provided
  if (companyId) {
    await call("associations.create", {
      fromType: "contacts", fromId: res.id,
      toType:   "companies", toId: companyId,
    }).catch(e => console.warn("Contact-company association failed:", e.message));
  }

  return {
    id:      res.id,
    name:    [properties.firstname, properties.lastname].filter(Boolean).join(" ") || "(no name)",
    email:   properties.email,
    phone:   properties.phone,
    role:    properties.jobtitle,
    ownerId: null,
  };
}

// ── DEALS ────────────────────────────────────────────────────────────────────
export async function getDealsForCompany(companyId) {
  const assoc = await call("deals.forCompany", {
    id: companyId,
    params: { limit: 500 },
  });
  const ids = (assoc.results || []).map(r => r.id);
  if (!ids.length) return [];

  const deals = [];
  for (const id of ids) {
    const d = await call("deals.get", {
      id,
      params: { properties: "dealname,dealstage,amount,closedate,description" },
    }).catch(() => null);
    if (d) deals.push(d);
    if (ids.length > 5) await sleep(150);
  }

  return deals.map(d => ({
    id:        d.id,
    name:      d.properties.dealname    || "",
    stage:     normalizeStage(d.properties.dealstage),
    pipeline:  pipelineForStage(d.properties.dealstage),
    stageId:   d.properties.dealstage   || "",
    amount:    parseFloat(d.properties.amount) || 0,
    closeDate: normalizeDate(d.properties.closedate),
    notes:     d.properties.description || "",
  }));
}

export async function createDeal(fields, companyId) {
  const res = await call("deals.create", {
    data: {
      properties: {
        dealname:    fields.name      || "",
        dealstage:   toHubSpotStage(fields.stage || "Quote Requested"),
        amount:      String(fields.amount || 0),
        closedate:   fields.closeDate || "",
        description: fields.notes     || "",
      },
    },
  });
  if (companyId && res.id) {
    await call("associations.create", {
      fromType: "deals", fromId: res.id,
      toType:   "companies", toId: companyId,
    }).catch(() => {});
  }
  return res;
}

export async function updateDeal(dealId, fields) {
  // Use the deal's pipeline (passed in fields) to map stage name to the
  // correct HubSpot stage ID. Without this, an ABM deal moved from "Quote
  // Sent" to "Closed Won" would get the Mattress Closed Won ID and silently
  // jump pipelines.
  const stageId = fields.pipeline === "abm"
    ? toHubSpotStageForPipeline(fields.stage || "Quote Sent", "abm")
    : toHubSpotStage(fields.stage || "Quote Requested");
  return call("deals.update", {
    id: dealId,
    data: {
      properties: {
        dealname:    fields.name      || "",
        dealstage:   stageId,
        amount:      String(fields.amount || 0),
        closedate:   fields.closeDate || "",
        description: fields.notes     || "",
      },
    },
  });
}

export async function deleteDeal(dealId) {
  return call("deals.delete", { id: dealId });
}

// ── OWNERS ───────────────────────────────────────────────────────────────────
export async function getOwners() {
  try {
    const res = await call("owners.list", { params: { limit: 100 } });
    return (res.results || []).map(o => ({
      id:    o.id,
      email: o.email || "",
      name:  [o.firstName, o.lastName].filter(Boolean).join(" ") || o.email || "Unknown",
    }));
  } catch (e) {
    console.warn("getOwners failed:", e.message);
    return [];
  }
}

// ── ACTIVITIES (Notes / Calls / Emails) ──────────────────────────────────────
export async function logNote(body, companyId, contactId) {
  const res = await call("activities.create", {
    data: { properties: { hs_note_body: body, hs_timestamp: new Date().toISOString() } },
  });
  if (res.id) {
    if (companyId) await call("associations.create", { fromType:"notes", fromId:res.id, toType:"companies", toId:companyId }).catch(()=>{});
    if (contactId) await call("associations.create", { fromType:"notes", fromId:res.id, toType:"contacts",  toId:contactId }).catch(()=>{});
  }
  return res;
}

export async function logCall(summary, companyId, contactId) {
  const res = await call("calls.create", {
    data: {
      properties: {
        hs_call_body:      summary,
        hs_call_status:    "COMPLETED",
        hs_call_direction: "OUTBOUND",
        hs_timestamp:      new Date().toISOString(),
        hs_call_duration:  0,
      },
    },
  });
  if (res.id) {
    if (companyId) await call("associations.create", { fromType:"calls", fromId:res.id, toType:"companies", toId:companyId }).catch(()=>{});
    if (contactId) await call("associations.create", { fromType:"calls", fromId:res.id, toType:"contacts",  toId:contactId }).catch(()=>{});
  }
  return res;
}

// Log a sent email as a HubSpot engagement. NOTE: This LOGS the email as a
// record of activity -- it does NOT actually send mail. To physically send,
// HubSpot requires a connected mailbox per user (Microsoft 365 / Gmail
// integration), which is a HubSpot config setup outside the scope of this API.
//
// Required properties per HubSpot docs:
//   - hs_email_direction: "EMAIL" (outgoing) or "INCOMING_EMAIL"
//   - hs_timestamp: ISO timestamp
//   - hs_email_headers: a JSON-stringified object containing from/to/cc/bcc
//     arrays. HubSpot REJECTS top-level hs_email_to_email / hs_email_from_email
//     properties -- they must be nested inside hs_email_headers instead.
// Optional but useful:
//   - hs_email_subject, hs_email_text
//
// `sender` is { email, firstName, lastName } -- normally the logged-in rep.
// Falls back to a generic ABM placeholder if not provided.
export async function logEmail(to, subject, body, contactId, sender) {
  const from = sender || {
    email: "noreply@abminternational.com",
    firstName: "ABM",
    lastName: "Sales",
  };
  // HubSpot expects header values as a JSON-stringified blob. The "from"
  // field is required by HubSpot validation -- we always supply name+email
  // so the log entry in HubSpot shows the rep who sent the email.
  const headers = JSON.stringify({
    from: {
      email:     from.email     || "noreply@abminternational.com",
      firstName: from.firstName || "ABM",
      lastName:  from.lastName  || "Sales",
    },
    to: [{ email: to }],
    cc: [],
    bcc: [],
  });

  const res = await call("emails.create", {
    data: {
      properties: {
        hs_email_direction: "EMAIL",  // outbound
        hs_email_subject:   subject,
        hs_email_text:      body,
        hs_email_headers:   headers,
        hs_timestamp:       new Date().toISOString(),
      },
    },
  });
  if (res.id && contactId) {
    await call("associations.create", { fromType:"emails", fromId:res.id, toType:"contacts", toId:contactId }).catch(()=>{});
  }
  return res;
}

// Constant: how far back to pull activity (6 months in milliseconds)
const ACTIVITY_LOOKBACK_MS = 6 * 30 * 24 * 60 * 60 * 1000;
const ACTIVITY_LIMIT = 100;

// Build the timestamp filter for the search endpoint -- "give me items where
// hs_timestamp >= 6 months ago".
function activitySinceFilter() {
  return {
    propertyName: "hs_timestamp",
    operator: "GTE",
    value: String(Date.now() - ACTIVITY_LOOKBACK_MS),
  };
}

// Format a HubSpot timestamp (ISO string) for display.
function fmtActivityDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

// Pull notes + calls + emails for a company over the last 6 months.
// Uses HubSpot's search endpoints with association filters -- the previous
// implementation used activities.list with an `associations` query param,
// which HubSpot ignores entirely.
export async function getActivitiesForCompany(companyId) {
  if (!companyId) return [];
  try {
    const filter = {
      filterGroups: [{
        filters: [
          { propertyName: "associations.company", operator: "EQ", value: String(companyId) },
          activitySinceFilter(),
        ],
      }],
      sorts: [{ propertyName: "hs_timestamp", direction: "DESCENDING" }],
      limit: ACTIVITY_LIMIT,
    };

    const [notesRes, callsRes, emailsRes, meetingsRes] = await Promise.all([
      call("notes.search", {
        data: { ...filter, properties: ["hs_note_body", "hs_timestamp"] },
      }).catch(() => ({ results: [] })),
      call("calls.search", {
        data: { ...filter, properties: ["hs_call_body", "hs_call_title", "hs_timestamp", "hs_call_status"] },
      }).catch(() => ({ results: [] })),
      call("emails.search", {
        data: { ...filter, properties: ["hs_email_subject", "hs_email_text", "hs_timestamp"] },
      }).catch(() => ({ results: [] })),
      call("meetings.search", {
        data: { ...filter, properties: ["hs_meeting_title","hs_meeting_body","hs_meeting_outcome","hs_activity_type","hs_timestamp"] },
      }).catch(() => ({ results: [] })),
    ]);

    const notes = (notesRes.results || []).map(n => ({
      id:      "note_" + n.id,
      type:    "note",
      ts:      n.properties.hs_timestamp,
      date:    fmtActivityDate(n.properties.hs_timestamp),
      summary: n.properties.hs_note_body || "",
      content: n.properties.hs_note_body || "",
    }));
    const calls = (callsRes.results || []).map(c => ({
      id:      "call_" + c.id,
      type:    "call",
      ts:      c.properties.hs_timestamp,
      date:    fmtActivityDate(c.properties.hs_timestamp),
      summary: c.properties.hs_call_title || c.properties.hs_call_body || "",
      content: [c.properties.hs_call_title, c.properties.hs_call_body].filter(Boolean).join("\n\n"),
    }));
    const emails = (emailsRes.results || []).map(e => ({
      id:      "email_" + e.id,
      type:    "email",
      ts:      e.properties.hs_timestamp,
      date:    fmtActivityDate(e.properties.hs_timestamp),
      summary: e.properties.hs_email_subject || e.properties.hs_email_text || "",
      content: [e.properties.hs_email_subject, e.properties.hs_email_text].filter(Boolean).join("\n\n"),
    }));

    // F2F (in-person visit) meetings -- filter client-side for activity_type
    // prefix "F2F" because HubSpot's CONTAINS_TOKEN match isn't reliable.
    const visits = (meetingsRes.results || [])
      .filter(m => (m.properties.hs_activity_type || "").trim().toUpperCase().startsWith("F2F"))
      .map(m => ({
        id:      "meeting_" + m.id,
        type:    "visit",
        ts:      m.properties.hs_timestamp,
        date:    fmtActivityDate(m.properties.hs_timestamp),
        summary: m.properties.hs_meeting_title || m.properties.hs_meeting_body || "",
        content: [m.properties.hs_meeting_title, m.properties.hs_meeting_body, m.properties.hs_meeting_outcome].filter(Boolean).join("\n\n"),
      }));

    // Sort by raw timestamp DESC (newest first). Fallback to date-string sort
    // if ts is missing for any reason.
    return [...notes, ...calls, ...emails, ...visits].sort((a, b) => {
      const aT = a.ts ? new Date(a.ts).getTime() : 0;
      const bT = b.ts ? new Date(b.ts).getTime() : 0;
      return bT - aT;
    });
  } catch (e) {
    console.warn("getActivitiesForCompany failed:", e.message);
    return [];
  }
}

export async function getActivitiesForContact(contactId, fallbackCompanyId) {
  if (!contactId) {
    if (fallbackCompanyId) return getActivitiesForCompany(fallbackCompanyId);
    return [];
  }
  try {
    const filter = {
      filterGroups: [{
        filters: [
          { propertyName: "associations.contact", operator: "EQ", value: String(contactId) },
          activitySinceFilter(),
        ],
      }],
      sorts: [{ propertyName: "hs_timestamp", direction: "DESCENDING" }],
      limit: ACTIVITY_LIMIT,
    };

    const [notesRes, callsRes, emailsRes, meetingsRes] = await Promise.all([
      call("notes.search", {
        data: { ...filter, properties: ["hs_note_body", "hs_timestamp"] },
      }).catch(() => ({ results: [] })),
      call("calls.search", {
        data: { ...filter, properties: ["hs_call_body", "hs_call_title", "hs_timestamp", "hs_call_status"] },
      }).catch(() => ({ results: [] })),
      call("emails.search", {
        data: { ...filter, properties: ["hs_email_subject", "hs_email_text", "hs_timestamp"] },
      }).catch(() => ({ results: [] })),
      call("meetings.search", {
        data: { ...filter, properties: ["hs_meeting_title","hs_meeting_body","hs_meeting_outcome","hs_activity_type","hs_timestamp"] },
      }).catch(() => ({ results: [] })),
    ]);

    const notes = (notesRes.results || []).map(n => ({
      id:      "note_" + n.id,
      type:    "note",
      ts:      n.properties.hs_timestamp,
      date:    fmtActivityDate(n.properties.hs_timestamp),
      summary: n.properties.hs_note_body || "",
      content: n.properties.hs_note_body || "",
    }));
    const calls = (callsRes.results || []).map(c => ({
      id:      "call_" + c.id,
      type:    "call",
      ts:      c.properties.hs_timestamp,
      date:    fmtActivityDate(c.properties.hs_timestamp),
      summary: c.properties.hs_call_title || c.properties.hs_call_body || "",
      content: [c.properties.hs_call_title, c.properties.hs_call_body].filter(Boolean).join("\n\n"),
    }));
    const emails = (emailsRes.results || []).map(e => ({
      id:      "email_" + e.id,
      type:    "email",
      ts:      e.properties.hs_timestamp,
      date:    fmtActivityDate(e.properties.hs_timestamp),
      summary: e.properties.hs_email_subject || e.properties.hs_email_text || "",
      content: [e.properties.hs_email_subject, e.properties.hs_email_text].filter(Boolean).join("\n\n"),
    }));

    const visits = (meetingsRes.results || [])
      .filter(m => (m.properties.hs_activity_type || "").trim().toUpperCase().startsWith("F2F"))
      .map(m => ({
        id:      "meeting_" + m.id,
        type:    "visit",
        ts:      m.properties.hs_timestamp,
        date:    fmtActivityDate(m.properties.hs_timestamp),
        summary: m.properties.hs_meeting_title || m.properties.hs_meeting_body || "",
        content: [m.properties.hs_meeting_title, m.properties.hs_meeting_body, m.properties.hs_meeting_outcome].filter(Boolean).join("\n\n"),
      }));

    return [...notes, ...calls, ...emails, ...visits].sort((a, b) => {
      const aT = a.ts ? new Date(a.ts).getTime() : 0;
      const bT = b.ts ? new Date(b.ts).getTime() : 0;
      return bT - aT;
    });
  } catch (e) {
    console.warn("getActivitiesForContact failed:", e.message);
    return [];
  }
}

// ── ACTIVITY BY OWNER (for Calendar) ─────────────────────────────────────────
// Fetches notes, calls, and emails created by specific HubSpot owner(s) within
// a date range. Used by the Calendar to populate from logged HubSpot activity.
//
// ownerIds: array of HubSpot owner IDs (strings)
// startMs, endMs: epoch ms bounds (inclusive)
//
// Returns flat array of {type, ts, date, summary, ownerId} sorted newest-first.
//
// Uses the search endpoints. HubSpot uses `hubspot_owner_id` as the
// filterable owner property on notes, calls, and emails alike (despite
// what some older docs suggest about `hs_owner_id`).
export async function getActivitiesByOwners(ownerIds, startMs, endMs) {
  if (!ownerIds || ownerIds.length === 0) return [];

  const ownerStrs = ownerIds.map(String).filter(Boolean);
  if (ownerStrs.length === 0) return [];

  // Build filter for one object type. ownerProp is the property name to match.
  const buildSearch = (ownerProp) => {
    // We need: (owner IN [X, Y, ...]) AND ts >= start AND ts <= end.
    // BETWEEN operator returned 400s on notes/calls in some HubSpot accounts,
    // so we use two separate timestamp filters instead. All filters in one
    // group are AND'd together.
    return {
      filterGroups: [{
        filters: [
          { propertyName: ownerProp,      operator: "IN",  values: ownerStrs },
          { propertyName: "hs_timestamp", operator: "GTE", value: String(startMs) },
          { propertyName: "hs_timestamp", operator: "LTE", value: String(endMs) },
        ],
      }],
      sorts: [{ propertyName: "hs_timestamp", direction: "DESCENDING" }],
      limit: 100,
    };
  };

  try {
    const [notesRes, callsRes, emailsRes, meetingsRes] = await Promise.all([
      call("notes.search", {
        data: { ...buildSearch("hubspot_owner_id"), properties: ["hs_note_body","hs_timestamp","hubspot_owner_id"] },
      }).catch((e) => { console.warn("notes.search owner:", e.message); return { results: [] }; }),
      call("calls.search", {
        data: { ...buildSearch("hubspot_owner_id"), properties: ["hs_call_body","hs_call_title","hs_timestamp","hubspot_owner_id"] },
      }).catch((e) => { console.warn("calls.search owner:", e.message); return { results: [] }; }),
      call("emails.search", {
        data: { ...buildSearch("hubspot_owner_id"), properties: ["hs_email_subject","hs_email_text","hs_timestamp","hubspot_owner_id"] },
      }).catch((e) => { console.warn("emails.search owner:", e.message); return { results: [] }; }),
      // F2F (In-Person Visit) meetings. We pull ALL meetings server-side
      // (filtered by owner+time) then filter client-side for hs_activity_type
      // starting with "F2F" -- same pattern as getInPersonVisitCount because
      // HubSpot's CONTAINS_TOKEN with F2F* doesn't reliably match.
      call("meetings.search", {
        data: { ...buildSearch("hubspot_owner_id"), properties: ["hs_meeting_title","hs_meeting_body","hs_meeting_outcome","hs_activity_type","hs_timestamp","hubspot_owner_id"] },
      }).catch((e) => { console.warn("meetings.search owner:", e.message); return { results: [] }; }),
    ]);

    const fmt = (ts) => {
      if (!ts) return "";
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "";
      return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
    };
    const fmtTime = (ts) => {
      if (!ts) return "";
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "";
      return String(d.getHours()).padStart(2,"0") + ":" + String(d.getMinutes()).padStart(2,"0");
    };

    const notes = (notesRes.results || []).map(n => ({
      id:      "note_" + n.id,
      type:    "note",
      ts:      n.properties.hs_timestamp,
      date:    fmt(n.properties.hs_timestamp),
      time:    fmtTime(n.properties.hs_timestamp),
      summary: (n.properties.hs_note_body || "").replace(/<[^>]+>/g, "").slice(0, 200),
      content: n.properties.hs_note_body || "",
      ownerId: String(n.properties.hubspot_owner_id || ""),
      hsId:    n.id,
    }));
    const calls = (callsRes.results || []).map(c => ({
      id:      "call_" + c.id,
      type:    "call",
      ts:      c.properties.hs_timestamp,
      date:    fmt(c.properties.hs_timestamp),
      time:    fmtTime(c.properties.hs_timestamp),
      summary: c.properties.hs_call_title || (c.properties.hs_call_body || "").replace(/<[^>]+>/g, "").slice(0, 200),
      content: [c.properties.hs_call_title, c.properties.hs_call_body].filter(Boolean).join("\n\n"),
      ownerId: String(c.properties.hubspot_owner_id || ""),
      hsId:    c.id,
    }));
    const emails = (emailsRes.results || []).map(e => ({
      id:      "email_" + e.id,
      type:    "email",
      ts:      e.properties.hs_timestamp,
      date:    fmt(e.properties.hs_timestamp),
      time:    fmtTime(e.properties.hs_timestamp),
      summary: e.properties.hs_email_subject || (e.properties.hs_email_text || "").replace(/<[^>]+>/g, "").slice(0, 200),
      content: [e.properties.hs_email_subject, e.properties.hs_email_text].filter(Boolean).join("\n\n"),
      ownerId: String(e.properties.hubspot_owner_id || ""),
      hsId:    e.id,
    }));
    // F2F (In-Person Visit) meetings -- filter client-side for activity_type
    // prefix "F2F" because HubSpot's STARTS_WITH/CONTAINS_TOKEN on this prop
    // doesn't match reliably. Non-F2F meetings (Zoom, phone, etc.) are dropped
    // per user preference (Option A: in-person visits only on calendar).
    const visits = (meetingsRes.results || [])
      .filter(m => (m.properties.hs_activity_type || "").trim().toUpperCase().startsWith("F2F"))
      .map(m => ({
        id:      "meeting_" + m.id,
        type:    "visit",
        ts:      m.properties.hs_timestamp,
        date:    fmt(m.properties.hs_timestamp),
        time:    fmtTime(m.properties.hs_timestamp),
        summary: m.properties.hs_meeting_title || (m.properties.hs_meeting_body || "").replace(/<[^>]+>/g, "").slice(0, 200),
        content: [m.properties.hs_meeting_title, m.properties.hs_meeting_body, m.properties.hs_meeting_outcome].filter(Boolean).join("\n\n"),
        ownerId: String(m.properties.hubspot_owner_id || ""),
        hsId:    m.id,
      }));

    return [...notes, ...calls, ...emails, ...visits]
      .filter(a => a.date)
      .sort((a,b) => {
        const aT = a.ts ? new Date(a.ts).getTime() : 0;
        const bT = b.ts ? new Date(b.ts).getTime() : 0;
        return bT - aT;
      });
  } catch (e) {
    console.warn("getActivitiesByOwners failed:", e.message);
    return [];
  }
}

// ── ACTIVITY COUNTS (for Progress / Quota dashboard) ─────────────────────────
export async function getActivityCounts(ownerId, period = "month") {
  if (!ownerId) return { calls: 0, notes: 0, emails: 0 };

  const now = new Date();
  let start;
  if (period === "month") {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (period === "quarter") {
    const q = Math.floor(now.getMonth() / 3) * 3;
    start = new Date(now.getFullYear(), q, 1);
  } else {
    start = new Date(now.getFullYear(), 0, 1);
  }
  const startMs = start.getTime();

  const buildSearch = ownerProp => ({
    filterGroups: [{
      filters: [
        { propertyName: ownerProp, operator: "EQ", value: String(ownerId) },
        { propertyName: "hs_timestamp", operator: "GTE", value: String(startMs) },
      ],
    }],
    properties: ["hs_timestamp"],
    limit: 1,
  });

  let calls = 0, notes = 0, emails = 0;
  try {
    const c = await call("calls.search", { data: buildSearch("hubspot_owner_id") });
    calls = c.total || 0;
    await sleep(500);
    const n = await call("notes.search", { data: buildSearch("hubspot_owner_id") });
    notes = n.total || 0;
    await sleep(500);
    const e = await call("emails.search", { data: buildSearch("hubspot_owner_id") });
    emails = e.total || 0;
  } catch (err) {
    console.warn("Activity counts failed:", err.message);
  }
  console.log("HubSpot activity for owner " + ownerId + " (" + period + "): " + calls + " calls, " + notes + " notes, " + emails + " emails");
  return { calls, notes, emails };
}

// Compute the start-of-period epoch ms for "month" | "quarter" | "ytd".
// Shared by all metric functions so they all measure the same window.
function periodStartMs(period) {
  const now = new Date();
  let start;
  if (period === "month") {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (period === "quarter") {
    const q = Math.floor(now.getMonth() / 3) * 3;
    start = new Date(now.getFullYear(), q, 1);
  } else {
    start = new Date(now.getFullYear(), 0, 1);
  }
  return start.getTime();
}

// ── IN-PERSON VISITS ─────────────────────────────────────────────────────────
// Count of meetings owned by `ownerId` where hs_activity_type starts with "F2F".
// HubSpot's CONTAINS_TOKEN with F2F* didn't match, so we pull meetings filtered
// server-side by owner+timestamp, then filter client-side for the "F2F" prefix.
// Dataset is small (max 100-200 meetings per rep per month) so this is fine.
export async function getInPersonVisitCount(ownerId, period = "month") {
  if (!ownerId) return 0;
  const startMs = periodStartMs(period);
  try {
    const res = await call("meetings.search", {
      data: {
        filterGroups: [{
          filters: [
            { propertyName: "hubspot_owner_id", operator: "EQ", value: String(ownerId) },
            { propertyName: "hs_timestamp", operator: "GTE", value: String(startMs) },
          ],
        }],
        properties: ["hs_activity_type", "hs_timestamp"],
        limit: 200,
      },
    });
    const results = res.results || [];
    const f2fCount = results.filter(m => {
      const t = (m.properties.hs_activity_type || "").trim();
      return t.toUpperCase().startsWith("F2F");
    }).length;
    return f2fCount;
  } catch (err) {
    console.warn("In-person visits count failed for " + ownerId + ":", err.message);
    return 0;
  }
}

// ── QUOTES CREATED ───────────────────────────────────────────────────────────
// Count of deals where `ownerId` is the deal owner AND createdate is in period.
//
// HISTORY: An earlier version of this function used numeric millisecond filters
// which mis-compared against HubSpot's ISO string createdate, so we dropped to
// client-side date filtering. That fix worked for low-volume reps but broke for
// high-volume reps like Mark Poole (300+ deals lifetime): HubSpot capped results
// at 100 and returned them in default order (oldest-first), so recent deals
// were never seen and YTD count showed 0.
//
// FIX: Use HubSpot's GTE operator with an ISO string value -- which IS supported
// and DOES compare correctly (different from the broken numeric-ms approach).
// Also sort by createdate DESC and paginate if needed, so even reps with
// hundreds of in-period deals get accurate counts.
export async function getDealsCreatedCount(ownerId, period = "month") {
  if (!ownerId) return 0;
  const startMs = periodStartMs(period);
  const startIso = new Date(startMs).toISOString();
  let total = 0;
  let after = undefined;
  let pages = 0;
  const MAX_PAGES = 10;  // 10 pages × 100/page = up to 1000 deals; plenty for any single rep/period
  try {
    while (pages < MAX_PAGES) {
      const data = {
        filterGroups: [{
          filters: [
            { propertyName: "hubspot_owner_id", operator: "EQ",  value: String(ownerId) },
            { propertyName: "createdate",       operator: "GTE", value: startIso },
          ],
        }],
        sorts: [{ propertyName: "createdate", direction: "DESCENDING" }],
        properties: ["createdate"],
        limit: 100,
      };
      if (after) data.after = after;
      const res = await call("deals.search", { data });
      const results = res.results || [];
      // Defensive: double-check client-side in case HubSpot's filter ever
      // returns something off-window. Cheap to verify and catches edge cases.
      total += results.filter(d => {
        const cd = d.properties.createdate;
        if (!cd) return false;
        const ms = new Date(cd).getTime();
        return !isNaN(ms) && ms >= startMs;
      }).length;
      after = res.paging?.next?.after;
      if (!after || results.length < 100) break;
      pages++;
    }
    return total;
  } catch (err) {
    console.warn("Deals created count failed for " + ownerId + ":", err.message);
    return 0;
  }
}

// ── SALES GENERATED ──────────────────────────────────────────────────────────
// Sum of `amount` across deals where `ownerId` is owner AND dealstage is a
// Closed Won stage (Mattress or ABM pipeline) AND closedate is in period.
// Closed Won stage IDs are discovered dynamically from HubSpot on app boot via
// discoverPipelines() and exposed through getClosedWonStageIds().
//
// HISTORY + FIX: same situation as getDealsCreatedCount above -- earlier
// numeric-ms filters mis-compared against HubSpot's ISO closedate, fixed by
// dropping to client-side, which then broke for high-volume reps because the
// 100-deal cap returned oldest-first.
//
// Current fix: HubSpot's GTE operator with an ISO string DOES work correctly
// for closedate. Combine with sort DESC + pagination for full coverage.
export async function getSalesGenerated(ownerId, period = "month") {
  if (!ownerId) return 0;
  const startMs = periodStartMs(period);
  const startIso = new Date(startMs).toISOString();
  const closedWonIds = getClosedWonStageIds();
  let total = 0;
  let after = undefined;
  let pages = 0;
  const MAX_PAGES = 10;  // up to 1000 won deals per rep per period
  try {
    while (pages < MAX_PAGES) {
      const data = {
        filterGroups: [{
          filters: [
            { propertyName: "hubspot_owner_id", operator: "EQ",  value: String(ownerId) },
            { propertyName: "dealstage",        operator: "IN",  values: closedWonIds },
            { propertyName: "closedate",        operator: "GTE", value: startIso },
          ],
        }],
        sorts: [{ propertyName: "closedate", direction: "DESCENDING" }],
        properties: ["amount", "closedate"],
        limit: 100,
      };
      if (after) data.after = after;
      const res = await call("deals.search", { data });
      const results = res.results || [];
      for (const d of results) {
        // Defensive client-side date check (cheap; catches edge cases)
        const cd = d.properties.closedate;
        if (!cd) continue;
        const closedMs = new Date(cd).getTime();
        if (isNaN(closedMs) || closedMs < startMs) continue;
        const amt = parseFloat(d.properties.amount);
        if (!isNaN(amt)) total += amt;
      }
      after = res.paging?.next?.after;
      if (!after || results.length < 100) break;
      pages++;
    }
    return total;
  } catch (err) {
    console.warn("Sales generated failed for " + ownerId + ":", err.message);
    return 0;
  }
}

// ── TASKS ────────────────────────────────────────────────────────────────────
export async function createTask(fields, companyId) {
  // Build assigned_owner_id only when caller explicitly passes one. This lets
  // admins assign tasks on behalf of reps (e.g. via future "delegate" feature).
  const props = {
    hs_task_subject: fields.title || "",
    hs_task_body:    fields.note  || "",
    hs_task_status:  fields.done ? "COMPLETED" : "NOT_STARTED",
    hs_task_type:    (fields.type || "TODO").toUpperCase(),
    hs_timestamp:    fields.dueDate
      ? new Date(fields.dueDate + "T12:00:00").toISOString()
      : new Date().toISOString(),
  };
  if (fields.ownerId) props.hubspot_owner_id = String(fields.ownerId);

  const res = await call("tasks.create", { data: { properties: props } });
  if (companyId && res.id) {
    await call("associations.create", {
      fromType: "tasks", fromId: res.id,
      toType:   "companies", toId: companyId,
    }).catch(() => {});
  }
  return res;
}

// Update an existing HubSpot task (by HubSpot task ID).
export async function updateTaskInHubSpot(taskHsId, fields) {
  const props = {};
  if (fields.title    !== undefined) props.hs_task_subject = fields.title || "";
  if (fields.note     !== undefined) props.hs_task_body    = fields.note  || "";
  if (fields.type     !== undefined) props.hs_task_type    = (fields.type || "TODO").toUpperCase();
  if (fields.done     !== undefined) props.hs_task_status  = fields.done ? "COMPLETED" : "NOT_STARTED";
  if (fields.dueDate  !== undefined) {
    props.hs_timestamp = fields.dueDate
      ? new Date(fields.dueDate + "T12:00:00").toISOString()
      : new Date().toISOString();
  }
  if (fields.ownerId !== undefined) props.hubspot_owner_id = fields.ownerId ? String(fields.ownerId) : "";

  return call("tasks.update", { id: taskHsId, data: { properties: props } });
}

// Delete a HubSpot task by ID.
export async function deleteTaskInHubSpot(taskHsId) {
  return call("tasks.delete", { id: taskHsId });
}

// Pull tasks owned by one or more HubSpot owner IDs.
// Returns flat array of {id, hsId, title, note, type, dueDate, done, ownerId, companyId, companyName}.
// `companyId` and `companyName` populate from associations if available.
export async function getTasksByOwners(ownerIds, opts = {}) {
  if (!ownerIds || ownerIds.length === 0) return [];
  const ownerStrs = ownerIds.map(String).filter(Boolean);
  if (ownerStrs.length === 0) return [];

  try {
    const res = await call("tasks.search", {
      data: {
        filterGroups: [{
          filters: [
            { propertyName: "hubspot_owner_id", operator: "IN", values: ownerStrs },
          ],
        }],
        sorts: [{ propertyName: "hs_timestamp", direction: "DESCENDING" }],
        properties: [
          "hs_task_subject","hs_task_body","hs_task_status","hs_task_type",
          "hs_timestamp","hubspot_owner_id",
        ],
        limit: opts.limit || 200,
      },
    });
    const results = res.results || [];

    // Map HubSpot task -> CRM task shape
    return results.map(t => {
      const p = t.properties || {};
      const ts = p.hs_timestamp ? new Date(p.hs_timestamp) : null;
      const dueDate = ts && !isNaN(ts.getTime())
        ? ts.getUTCFullYear() + "-" + String(ts.getUTCMonth()+1).padStart(2,"0") + "-" + String(ts.getUTCDate()).padStart(2,"0")
        : "";
      const typeLower = (p.hs_task_type || "todo").toLowerCase();
      return {
        id:       "hs_" + t.id,
        hsId:     t.id,
        title:    p.hs_task_subject || "(no title)",
        note:     p.hs_task_body || "",
        type:     typeLower === "todo" ? "call" : typeLower, // map HubSpot "TODO" to "call" default
        dueDate,
        done:     p.hs_task_status === "COMPLETED",
        ownerId:  String(p.hubspot_owner_id || ""),
      };
    });
  } catch (e) {
    console.warn("getTasksByOwners failed:", e.message);
    return [];
  }
}
